import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getSettings,
  updateSettings,
} from "../../../../../../../services/settingsServices";

import {
  uploadSettingsDefaultLicCot,
  toAbsolutePhotoUrl,
} from "../../../../../../../services/settingsPhotoServices";

import {
  DEFAULT_PAYMENT_IMAGE,
  DEFAULT_PAYMENT_SETTINGS,
  normalizePaymentPayload,
  unwrapSettings,
  validateImageFile,
  validatePaymentSettings,
  withBust,
} from "../utils/paiementSettingsUtils";

export function usePaiementSettings() {
  const [settings, setSettings] = useState(DEFAULT_PAYMENT_SETTINGS);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");

  const [outputFormat, setOutputFormat] = useState("webp");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [imgVersion, setImgVersion] = useState(0);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getSettings();
      const first = unwrapSettings(data);

      if (first) {
        setSettings({
          ...DEFAULT_PAYMENT_SETTINGS,
          ...first,
          max_payments: first?.max_payments || DEFAULT_PAYMENT_SETTINGS.max_payments,
          payment_interval:
            first?.payment_interval || DEFAULT_PAYMENT_SETTINGS.payment_interval,
        });

        setImgVersion((value) => value + 1);
      }
    } catch (error) {
      console.error("Erreur chargement paramètres paiement :", error);
      toast.error("Impossible de charger les paramètres de paiement.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (!selectedFile) {
      setFilePreview("");
      return undefined;
    }

    const url = URL.createObjectURL(selectedFile);
    setFilePreview(url);

    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  const imageSrc = useMemo(() => {
    const raw =
      settings.photo_url_default_lic_cot?.trim() || DEFAULT_PAYMENT_IMAGE;

    const absoluteUrl = toAbsolutePhotoUrl(raw) || DEFAULT_PAYMENT_IMAGE;

    return withBust(absoluteUrl, imgVersion);
  }, [settings.photo_url_default_lic_cot, imgVersion]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();

    const payload = normalizePaymentPayload(settings);
    const errorMessage = validatePaymentSettings(payload);

    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    try {
      setSaving(true);

      await updateSettings(payload);

      setSettings(payload);
      toast.success("Paramètres de paiement mis à jour avec succès.");
    } catch (error) {
      console.error("Erreur sauvegarde paramètres paiement :", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data ||
          "Erreur lors de la mise à jour des paramètres de paiement."
      );
    } finally {
      setSaving(false);
    }
  };

  const openUploadModal = () => {
    setSelectedFile(null);
    setFilePreview("");
    setOutputFormat("webp");
    setUploadProgress(0);
    setIsUploadModalOpen(true);
  };

  const closeUploadModal = () => {
    if (uploading) return;

    setIsUploadModalOpen(false);
    setSelectedFile(null);
    setFilePreview("");
    setUploadProgress(0);
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;

    if (!file) {
      setSelectedFile(null);
      return;
    }

    const errorMessage = validateImageFile(file);

    if (errorMessage) {
      toast.error(errorMessage);
      event.target.value = "";
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const handleUploadSubmit = async (event) => {
    event?.preventDefault?.();

    const errorMessage = validateImageFile(selectedFile);

    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const updated = await uploadSettingsDefaultLicCot(selectedFile, {
        format: outputFormat,
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;

          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );

          setUploadProgress(percent);
        },
      });

      const newUrl = updated?.url || updated?.imageUrl || "";

      if (!newUrl) {
        toast.error("Réponse upload invalide : URL manquante.");
        return;
      }

      setSettings((prev) => ({
        ...prev,
        photo_url_default_lic_cot: newUrl,
      }));

      setImgVersion((value) => value + 1);

      toast.success("Image par défaut mise à jour avec succès.");
      closeUploadModal();
    } catch (error) {
      console.error("Erreur upload image paiement :", error?.response?.data || error);
      toast.error("Erreur lors de l’upload de l’image.");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return {
    settings,
    loading,
    saving,

    imageSrc,

    isUploadModalOpen,
    selectedFile,
    filePreview,
    outputFormat,
    setOutputFormat,
    uploading,
    uploadProgress,

    fetchSettings,
    handleChange,
    handleSubmit,

    openUploadModal,
    closeUploadModal,
    handleFileChange,
    handleUploadSubmit,
  };
}